import { useApiContext } from '@customink/project-service-react-components';
import type {
  BusinessAddressWName,
  CreateECMCustomerDto,
} from '@customink/project-service-react-components/src/services/accounts/types';
import { useCallback } from 'react';

import { useAuth } from '@/contexts/auth/AuthContext';
import { useGetCustomer } from '@/hooks/Accounts/TaxExemptionECM/useGetCustomer';

const transformToCustomerDto = (
  { organizationName: name, ...businessAddress }: BusinessAddressWName,
  emailAddress: string,
): CreateECMCustomerDto => ({
  name,
  emailAddress,
  businessAddress: {
    country: 'US',
    region: businessAddress.region,
    city: businessAddress.city,
    postalCode: businessAddress.postalCode,
    line1: businessAddress.line1,
    line2: businessAddress.line2,
  },
});

export const useCreateOrUpdateCustomer = () => {
  const api = useApiContext();
  const { personalAccount, user } = useAuth();
  const { data: customer } = useGetCustomer();
  const [triggerCreate, createResult] =
    api.useCreateAccountTaxExemptionCustomerMutation({
      skip: !personalAccount?.id || !user?.id,
    });
  const [triggerUpdate, updateResult] =
    api.useUpdateAccountTaxExemptionCustomerMutation({
      skip: !personalAccount?.id || !user?.id,
    });
  const trigger = useCallback(
    (businessAddress: BusinessAddressWName) => {
      if (customer?.statusCode === 404) {
        return triggerCreate({
          accountId: personalAccount?.id as string,
          customer: transformToCustomerDto(
            businessAddress,
            user?.email as string,
          ),
        });
      } else {
        return triggerUpdate({
          customerId: customer.id,
          customer: transformToCustomerDto(
            businessAddress,
            user?.email as string,
          ),
        });
      }
    },
    [
      personalAccount?.id,
      customer?.statusCode,
      customer?.id,
      triggerCreate,
      triggerUpdate,
    ],
  );
  return {
    trigger,
    isLoading: createResult.isLoading || updateResult.isLoading,
    isError: createResult.isError || updateResult.isError,
  };
};
